/* eslint-disable @typescript-eslint/no-explicit-any */
// react-i18next + TS 4.9.5 workaround — see src/i18n/index.ts.
import React, { useMemo, useState } from 'react';
import { useTranslation as useTranslationUntyped } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { ProfileType } from '../../types/enums';
import { AvailableProfileDto } from '../../services/api';
import ElevationModal from '../ElevationModal/ElevationModal';
import styles from './ProfileSwitcher.module.css';

const useTranslation = useTranslationUntyped as any;

interface ProfileSwitcherProps {
    /** Closes the parent menu after a switch action. */
    onClose?: () => void;
}

/**
 * Auth v2 — in-portal profile switcher (AB#1435).
 *
 * Decodes the current access token's `availableProfiles` claim and renders a
 * list of the user's profiles (filtered per PO-D-002: Customer is mobile-only
 * until Q3 2026). Clicking a non-active profile calls `switchProfileV2` from
 * `useAuth`, which mints a new access token, resets client caches, and
 * navigates to the new role's home.
 *
 * SuperAdmin elevation: when the server returns 403 Elevation, this component
 * surfaces a lightweight notice. The full password re-entry modal is
 * delivered in AB#1437.
 *
 * Hidden entirely when:
 *   - Auth v2 flag is off, OR
 *   - the user has fewer than 2 surface-able profiles (after filtering Customer).
 *
 * Keyboard / ARIA: inherits the parent account-menu's role="menu" context.
 * Each profile row is `role="menuitemradio"` with `aria-checked` on the active.
 */
const ProfileSwitcher: React.FC<ProfileSwitcherProps> = ({ onClose }) => {
    const { t } = useTranslation('auth');
    const { switchProfileV2, isAuthV2Enabled, loading } = useAuth();

    const { activeProfileId, profiles } = useMemo(() => readProfilesFromToken(), []);

    // AB#1437 — Elevation modal state. When the switcher gets 403 Elevation
    // back from the server, it stores the pending target and opens the modal.
    const [pendingElevationTarget, setPendingElevationTarget] = useState<number | null>(null);

    if (!isAuthV2Enabled) {
        return null;
    }

    // PO-D-002: Customer is mobile-only on web until Q3 2026 — filter out.
    const surfaceable = profiles.filter(p => p.type !== ProfileType.Customer);
    if (surfaceable.length < 2) {
        // Switcher only makes sense for multi-profile users.
        return null;
    }

    const handleSelect = async (profile: AvailableProfileDto) => {
        if (profile.id === activeProfileId || loading) return;

        const result = await switchProfileV2(profile.id);

        // AB#1437 — SuperAdmin elevation: open the password modal, remember
        // the pending target so we can retry on success.
        if (result && 'needsElevation' in result) {
            setPendingElevationTarget(profile.id);
            return;
        }

        if (onClose) onClose();
    };

    const handleElevationSuccess = async () => {
        const target = pendingElevationTarget;
        setPendingElevationTarget(null);
        if (target == null) return;
        // Retry the switch now that passwordVerifiedAt is fresh.
        const result = await switchProfileV2(target);
        if (result && 'needsElevation' in result) {
            // Shouldn't happen — the verify-password call minted a fresh token.
            // Surface nothing; user can retry manually.
            return;
        }
        if (onClose) onClose();
    };

    return (
        <div className={styles.switcherRoot} role="group" aria-label={t('switcher.ariaLabel')}>
            <div className={styles.heading}>{t('switcher.heading')}</div>
            <ul className={styles.profileList}>
                {surfaceable.map(profile => {
                    const isActive = profile.id === activeProfileId;
                    const isSuperAdmin = profile.type === ProfileType.SuperAdmin;
                    return (
                        <li
                            key={profile.id}
                            className={`${styles.profileRow} ${isActive ? styles.active : ''}`}
                            role="menuitemradio"
                            aria-checked={isActive}
                            tabIndex={isActive ? 0 : -1}
                            onClick={() => handleSelect(profile)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleSelect(profile);
                                }
                            }}
                        >
                            <div className={styles.roleCol}>
                                <span className={styles.roleLabel}>{translatedRole(t, profile.type)}</span>
                                {profile.businessName && (
                                    <span className={styles.businessLabel}>{profile.businessName}</span>
                                )}
                                {isSuperAdmin && (
                                    <span className={styles.elevationNote}>{t('switcher.elevationNote')}</span>
                                )}
                            </div>
                            {isActive && <span className={styles.activeMark} aria-hidden="true">✓</span>}
                        </li>
                    );
                })}
            </ul>
            {loading && <div className={styles.inFlight}>{t('switcher.inFlight')}</div>}
            <div className={styles.footerNote}>{t('switcher.footerNote')}</div>
            <ElevationModal
                open={pendingElevationTarget != null}
                onSuccess={handleElevationSuccess}
                onCancel={() => setPendingElevationTarget(null)}
            />
        </div>
    );
};

function translatedRole(t: any, type: ProfileType): string {
    switch (type) {
        case ProfileType.SalonOwner:
            return t('roles.salonOwner');
        case ProfileType.SalonStaff:
            return t('roles.salonStaff');
        case ProfileType.SuperAdmin:
            return t('roles.superAdmin');
        case ProfileType.Customer:
            return t('roles.customer');
        default:
            return String(type);
    }
}

/**
 * Reads `availableProfiles` + `userProfileId` (active id) from the JWT in
 * localStorage. Returns empty state on any parse failure — the parent
 * component is responsible for hiding the switcher in that case.
 */
function readProfilesFromToken(): { activeProfileId: number | null; profiles: AvailableProfileDto[] } {
    try {
        const token = localStorage.getItem('accessToken');
        if (!token) return { activeProfileId: null, profiles: [] };

        const payload = token.split('.')[1];
        const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));

        const active = parseInt(decoded['userProfileId'] ?? '', 10);
        const raw = decoded['availableProfiles'];
        if (!raw) return { activeProfileId: isNaN(active) ? null : active, profiles: [] };

        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!Array.isArray(parsed)) return { activeProfileId: isNaN(active) ? null : active, profiles: [] };

        const profiles: AvailableProfileDto[] = parsed.map((p: any) => ({
            id: typeof p.id === 'number' ? p.id : parseInt(p.id, 10),
            type: mapProfileType(p.type),
            businessId: p.businessId ?? null,
            businessName: p.businessName ?? null,
        }));

        return {
            activeProfileId: isNaN(active) ? null : active,
            profiles,
        };
    } catch {
        return { activeProfileId: null, profiles: [] };
    }
}

function mapProfileType(raw: unknown): ProfileType {
    if (typeof raw === 'number') return raw as ProfileType;
    if (typeof raw === 'string') {
        switch (raw) {
            case 'Customer': return ProfileType.Customer;
            case 'SalonOwner': return ProfileType.SalonOwner;
            case 'SalonStaff': return ProfileType.SalonStaff;
            case 'SuperAdmin': return ProfileType.SuperAdmin;
            default: return ProfileType.Customer;
        }
    }
    return ProfileType.Customer;
}

export default ProfileSwitcher;
